import {Tooltip} from 'platform/components';
import {Box, Heading, HStack, Icon, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {clamp} from 'ramda';
import {isNumber, round} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {PriceReportType} from '../../../../../types/PriceReportType';

type ClampedAccuracy = 0 | 1 | 2 | 3 | 4 | 5;

interface AccuracyChartProps extends TestIdProps {
  priceReport: PriceReportType;
}

export const AccuracyChart = (props: AccuracyChartProps) => {
  const {accuracy} = props.priceReport;
  if (!isNumber(accuracy)) {
    return null;
  }

  const clampedAccuracy = round(clamp(0, 5, accuracy)) as ClampedAccuracy;
  const label = match<ClampedAccuracy, string>(clampedAccuracy)
    .with(0, () => i18n.t('entity.accuracyChart.labels.rating.0'))
    .with(1, () => i18n.t('entity.accuracyChart.labels.rating.1'))
    .with(2, () => i18n.t('entity.accuracyChart.labels.rating.2'))
    .with(3, () => i18n.t('entity.accuracyChart.labels.rating.3'))
    .with(4, () => i18n.t('entity.accuracyChart.labels.rating.4'))
    .with(5, () => i18n.t('entity.accuracyChart.labels.rating.5'))
    .exhaustive();

  const color = match<ClampedAccuracy, ThemeColorPath>(clampedAccuracy)
    .with(0, () => 'palettes.neutral.40.100')
    .with(1, () => 'palettes.green.60.100')
    .with(2, () => 'palettes.green.60.100')
    .with(3, () => 'palettes.yellow.40.100')
    .with(4, () => 'palettes.orange.40.100')
    .with(5, () => 'palettes.red.40.100')
    .exhaustive();

  const neutralColor = 'palettes.neutral.40.100';

  return (
    <VStack spacing={2} align="center">
      <HStack spacing={1} align="center">
        <Text
          size="small"
          color="secondary"
          align="center"
          data-testid={suffixTestId('heading', props)}
        >
          {i18n.t('entity.accuracyChart.labels.heading')}
        </Text>
        <Tooltip
          description={i18n.t('entity.accuracyChart.labels.tooltip')}
          data-testid={suffixTestId('tooltip', props)}
        >
          <Icon
            value="action/help"
            color="text.secondary"
            size={4}
            data-testid={suffixTestId('tooltip', props)}
          />
        </Tooltip>
      </HStack>
      <HStack spacing={4} align="center" data-testid={suffixTestId('rating', props)}>
        <HStack spacing={2}>
          {Array.from({length: 5}).map((_, index) => (
            <Box
              // eslint-disable-next-line react/no-array-index-key
              key={index} // Stable value, so index is safe
              width={3}
              height={3}
              borderRadius="circular"
              backgroundColor={
                clampedAccuracy !== 0 && index <= 5 - clampedAccuracy ? color : neutralColor
              }
            />
          ))}
        </HStack>
        <Heading size={4} data-testid={suffixTestId('ratingLabel', props)}>
          {label}
        </Heading>
      </HStack>
    </VStack>
  );
};
